'use client';

import type { DateRange } from 'react-day-picker';
import { format, setHours, setMinutes, setSeconds } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { Calendar as CalendarIcon } from 'lucide-react';

import { useEffect, useMemo, useState } from 'react';
import { Button } from './Button';
import { Calendar } from './Calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './legacy/Popover';

// Update the list to include only US timezones + UTC
const usTimezones = [
  'UTC',
  'America/New_York', // Eastern
  'America/Chicago', // Central
  'America/Denver', // Mountain
  'America/Los_Angeles', // Pacific
];

type DateTimeRangePickerProps = {
  selected?: DateRange; // Expecting UTC dates if passed in
  onSelect?: (range: DateRange | undefined) => void; // Will call with UTC dates
};

export function DateTimeRangePicker({
  selected: initialSelected, // Should be UTC
  onSelect,
}: DateTimeRangePickerProps) {
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(initialSelected);
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');
  const [isOpen, setIsOpen] = useState(false);
  // Add timezone state
  const [selectedTimezone, setSelectedTimezone] = useState<string>(() => {
    // Try to get browser timezone, default to UTC
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch /* (e) */ {
      return 'UTC'; // Fallback
    }
  });

  // Update internal state if initialSelected (UTC) prop changes
  useEffect(() => {
    setSelectedRange(initialSelected);
    if (initialSelected?.from) {
      // Convert incoming UTC date to the selected timezone for display
      const zonedFrom = toZonedTime(initialSelected.from, selectedTimezone);
      setStartTime(format(zonedFrom, 'HH:mm'));
    }
    if (initialSelected?.to) {
      // Convert incoming UTC date to the selected timezone for display
      const zonedTo = toZonedTime(initialSelected.to, selectedTimezone);
      setEndTime(format(zonedTo, 'HH:mm'));
    }
    // Note: We don't update selectedTimezone based on initialSelected here
    // It uses the browser default or the user's last selection within the component.
  }, [initialSelected, selectedTimezone]);

  const handleSelect = (range: DateRange | undefined) => {
    setSelectedRange(range);
  };

  const handleTimeChange = (
    type: 'start' | 'end',
    value: string,
  ) => {
    if (type === 'start') {
      setStartTime(value);
    } else {
      setEndTime(value);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) { // Call onSelect when popover closes
      // Use const for non-reassigned variables
      const finalFromDate = selectedRange?.from;
      const finalToDate = selectedRange?.to;
      let finalFromUtc: Date | undefined;
      let finalToUtc: Date | undefined;

      if (finalFromDate) {
        const [hours, minutes] = startTime.split(':').map(Number);
        // Use finalFromDate here
        const localFrom = setSeconds(setMinutes(setHours(finalFromDate, hours || 0), minutes || 0), 0);
        finalFromUtc = fromZonedTime(localFrom, selectedTimezone);
      }
      if (finalToDate) {
        const [hours, minutes] = endTime.split(':').map(Number);
        // Use finalToDate here
        const localTo = setSeconds(setMinutes(setHours(finalToDate, hours || 0), minutes || 0), 59);
        finalToUtc = fromZonedTime(localTo, selectedTimezone);
      }

      onSelect?.({ from: finalFromUtc, to: finalToUtc });
    }
    setIsOpen(open);
  };

  const triggerLabel = useMemo(() => {
    if (selectedRange?.from) {
      const fromZoned = toZonedTime(selectedRange.from, selectedTimezone);
      const fromStr = `${format(fromZoned, 'LLL dd, y')} ${startTime}`;

      // Get dynamic abbreviation using Intl
      let tzAbbr = selectedTimezone; // Default
      try {
        tzAbbr = new Intl.DateTimeFormat(undefined, {
          timeZone: selectedTimezone,
          timeZoneName: 'short',
        }).formatToParts(fromZoned).find(part => part.type === 'timeZoneName')?.value || selectedTimezone;
      } catch (error) {
        console.error(`Error formatting timezone ${selectedTimezone}:`, error);
      }

      if (selectedRange.to) {
        const toZoned = toZonedTime(selectedRange.to, selectedTimezone);
        const toStr = `${format(toZoned, 'LLL dd, y')} ${endTime}`;
        return `${fromStr} - ${toStr} (${tzAbbr})`; // Use Intl abbreviation
      }
      return `${fromStr} (${tzAbbr})`; // Use Intl abbreviation
    }
    return 'Pick a date, time, and timezone';
  }, [selectedRange, startTime, endTime, selectedTimezone]);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="secondary">
          <CalendarIcon className="h-4 w-4" />
          <span className="text-body-sm-medium py-2.5">{triggerLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4 space-y-4">
        {' '}
        {/* Added padding and spacing */}
        <Calendar
          mode="range"
          selected={selectedRange}
          onSelect={handleSelect}
          initialFocus
          numberOfMonths={2}
        />
        {/* Combined container for time inputs and timezone */}
        <div className="flex justify-between items-end space-x-2 pt-2 border-t mt-2">
          {' '}
          {/* Use items-end and adjust spacing */}
          {/* Start Time Input */}
          <div className="flex flex-col space-y-1">
            <label htmlFor="start-time" className="text-xs font-medium text-gray-600">Start time</label>
            <input
              id="start-time"
              type="time"
              value={startTime}
              onChange={e => handleTimeChange('start', e.target.value)}
              className="border rounded px-2 py-1 text-sm w-28 h-8"
            />
          </div>
          {/* End Time Input */}
          <div className="flex flex-col space-y-1">
            <label htmlFor="end-time" className="text-xs font-medium text-gray-600">End time</label>
            <input
              id="end-time"
              type="time"
              value={endTime}
              onChange={e => handleTimeChange('end', e.target.value)}
              className="border rounded px-2 py-1 text-sm w-28 h-8"
            />
          </div>
          {/* Timezone Selector */}
          <div className="flex flex-col space-y-1">
            <label htmlFor="timezone-select" className="text-xs font-medium text-gray-600">Timezone</label>
            <select
              id="timezone-select"
              value={selectedTimezone}
              onChange={e => setSelectedTimezone(e.target.value)}
              className="border rounded px-2 py-1 text-sm min-w-[120px] h-8" // Adjust width as needed
            >
              {usTimezones.map((tz) => {
                const displayDate = selectedRange?.from || new Date();
                let tzAbbr = tz;
                try {
                  tzAbbr = new Intl.DateTimeFormat(undefined, {
                    timeZone: tz,
                    timeZoneName: 'short',
                  }).formatToParts(displayDate).find(part => part.type === 'timeZoneName')?.value || tz;
                } catch (error) {
                  console.error(`Error formatting timezone ${tz}:`, error);
                }
                return (
                  <option key={tz} value={tz}>
                    {tzAbbr}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
